import _forEach from 'lodash/forEach'
import _get from 'lodash/get'
import _isUndefined from 'lodash/isUndefined'
import _keys from 'lodash/keys'
import _replace from 'lodash/replace'
import { Map } from 'immutable'
import { TYPE_FEATURE as VIDEO_TYPE_FEATURE } from '../services/video/index'
import {
  isFast404,
  isVideo,
} from '../services/url/index'

/**
 * The NewRelic library for this environment
 * @type {import('newrelic')|undefined}
 */
let _newrelic

/**
 * Set the NewRelic library that works for this runtime
 * @param {import('newrelic')} newrelic The NewRelic library
 * @returns The newrelic object which was passed in
 */
export function setNewRelicLibrary (newrelic) {
  _newrelic = newrelic
  return _newrelic
}

/**
 * Get the NewRelic library that works for this runtime
 * @return {import('newrelic')} The NewRelic library
 */
export function getNewRelicLibrary () {
  return _newrelic
}

/**
 * Add release information using the NewRelic browser API
 * @param {Object} options The options
 * @param {String} options.name The application name
 * @param {String} options.version The application version
 * @param {*} [options.newrelic] The NewRelic library defaults to singleton
 */
export function addRelease (options = {}) {
  const { name = 'web-app', version = '0.0.0', newrelic = getNewRelicLibrary() } = options
  if (!newrelic) {
    return
  }
  newrelic.addRelease(name, version)
}

/**
 * Set route information using the NewRelic browser api
 * @param {Object} options The options
 * @param {Map} [options.auth] The auth state
 * @param {Object} [options.query] A location query object
 * @param {String} options.pathname A location pathname
 * @param {import('newrelic')} [options.newrelic] The NewRelic library defaults to singleton
 */
export function setRoute (options = {}) {
  const {
    auth, query, pathname, newrelic = getNewRelicLibrary(),
  } = options
  if (!newrelic) {
    return
  }
  let authToken
  if (Map.isMap(auth)) {
    authToken = auth.get('jwt')
    const {
      uid, country, roles, subscriptions,
    } = auth.toJS()
    if (uid && uid > 0) {
      if (newrelic.setUserID) {
        newrelic.setUserID(uid)
      } else {
        newrelic.setCustomAttribute('userId', uid)
      }
    }
    // Create an auth attribute object that matches the server side implmentation
    const authAsAttributes = {
      userCountry: country || undefined,
      uid,
      roles: JSON.stringify(roles),
      userRoles: JSON.stringify(Array.isArray(roles) ? roles.sort() : roles),
      userSubscriptions: JSON.stringify(Array.isArray(subscriptions)
        ? subscriptions.sort() : subscriptions),
    }
    // Process authAsAttributes using the NewRelic setCustomAttribute browser api
    _forEach(_keys(authAsAttributes), (k) => {
      const v = authAsAttributes[k]
      if (_isUndefined(v)) {
        return
      }
      newrelic.setCustomAttribute(k, v)
    })
  }
  const name = getName({ pathname, query, authToken })
  newrelic.setPageViewName(name)
  newrelic.setCurrentRouteName(name)
}

/**
 * Set transaction information which occurs on the server side
 * @param {Object} options The options
 * @param {Object} options.query A location query object
 * @param {String} options.pathname A location pathname
 * @param {String} [options.authToken] A JWT Token
 * @param {import('newrelic')} [options.newrelic] The NewRelic library defaults to singleton
 */
export function setTransaction (options = {}) {
  const {
    pathname, query, authToken, newrelic = getNewRelicLibrary(),
  } = options
  if (!newrelic) {
    return
  }
  const name = getName({ pathname, query, authToken })
  newrelic.setTransactionName(name)
}

/**
 * Get a transaction name based on path, query and authToken
 * @param {Object} options The options
 * @param {Object} options.query A location query object
 * @param {String} options.pathname A location pathname
 * @param {String} [options.authToken] A JWT Token
 * @returns {String} The transaction name
 */
export function getName (options = {}) {
  const { query, pathname, authToken } = options
  // Perform all work before we get to the transaction section
  if (isFast404(pathname)) {
    return 'fast-404'
  } if (pathname === '/' && !authToken) {
    return 'home'
  } if (pathname === '/' && authToken) {
    return 'member-home'
  } if (isVideo(pathname)) {
    const fullplayer = _get(query, 'fullplayer')
    if (fullplayer === VIDEO_TYPE_FEATURE) {
      return 'video/:id/feature'
    }
    return 'video/:id/preview'
  }
  return _replace(pathname || '', /^\//, '')
}
